import React, { useState, useEffect, useMemo } from 'react';
import { showToast } from '../../utils/toaster';
import { generateRandomPath } from '../../utils/stringUtils';

interface RoutingStepProps {
  provider: string;
  initialSelectedHostPort?: string;
  initialSelectedPrefix?: string;
  initialCustomPath?: string;
  initialName?: string;
  initialDescription?: string;
  onChange: (data: {
    selectedHostPort: string;
    selectedPrefix: string;
    customPath: string;
    listen_address: string;
    route: string;
    name: string;
    description: string;
  }) => void;
  onNext: () => void;
  onBack: () => void;
  onCancel: () => void;
}

interface ChannelPrefixInfo {
  id: string;
  name: string;
  prefix: string;
}

interface ChannelRoutingConfig {
  available_listen_addresses: string[];
  channel_path_prefix: ChannelPrefixInfo[];
  pipe_path_prefix: ChannelPrefixInfo[];
}

const RoutingStep: React.FC<RoutingStepProps> = ({
  provider,
  initialSelectedHostPort = '',
  initialSelectedPrefix = '',
  initialCustomPath = '',
  initialName = '',
  initialDescription = '',
  onChange,
  onNext,
  onBack,
  onCancel,
}) => {
  const [routingConfig, setRoutingConfig] = useState<ChannelRoutingConfig | null>(null);
  const [selectedHostPort, setSelectedHostPort] = useState<string>(initialSelectedHostPort);
  const [selectedPrefix, setSelectedPrefix] = useState<string>(initialSelectedPrefix);
  const [customPath, setCustomPath] = useState<string>(initialCustomPath || generateRandomPath());
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Compute full route for display
  const fullRouteDisplay = useMemo(() => {
    if (selectedHostPort && selectedPrefix && customPath && customPath.trim()) {
      const normalizedPath = customPath.startsWith('/') ? customPath.substring(1) : customPath;
      return `${selectedHostPort}${selectedPrefix}/${normalizedPath}`;
    }
    return '(select address, prefix, and enter custom path)';
  }, [selectedHostPort, selectedPrefix, customPath]);

  // Fetch routing configuration on mount
  useEffect(() => {
    const fetchRoutingConfig = async () => {
      try {
        const response = await fetch('/api/v1/config/channel-routing');
        if (!response.ok) {
          throw new Error('Failed to fetch routing configuration');
        }
        const config: ChannelRoutingConfig = await response.json();
        setRoutingConfig(config);

        // Auto-select first options if not already set
        if (!initialSelectedHostPort && config.available_listen_addresses.length > 0) {
          setSelectedHostPort(config.available_listen_addresses[0]);
        }
        if (!initialSelectedPrefix && config.pipe_path_prefix.length > 0) {
          setSelectedPrefix(config.pipe_path_prefix[0].prefix);
        }
      } catch (error: any) {
        showToast('error', error.message || 'Failed to load routing configuration');
      }
    };

    fetchRoutingConfig();
  }, [initialSelectedHostPort, initialSelectedPrefix]);

  const validate = () => {
    const errors: string[] = [];

    if (!selectedHostPort) {
      errors.push('Listen address is required');
    }
    if (!selectedPrefix) {
      errors.push('Channel prefix is required');
    }
    if (!customPath.trim()) {
      errors.push('Custom path is required');
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleNext = () => {
    if (!validate()) {
      return;
    }

    // Build listen_address and route
    // Keep the scheme in listen_address to match dropdown values
    const normalizedPath = customPath.startsWith('/') ? customPath : `/${customPath}`;
    const route = `${selectedPrefix}${normalizedPath}`;

    onChange({
      selectedHostPort,
      selectedPrefix,
      customPath,
      listen_address: selectedHostPort, // Store with scheme to match dropdown
      route,
      name: initialName,
      description: initialDescription,
    });

    onNext();
  };

  return (
    <div>
      <h5 className="mb-3">
        <i className="fas fa-cog mr-2"></i>
        Configure Pipe
      </h5>
      <p className="text-muted mb-3" style={{ fontSize: '14px' }}>
        Configure the basic settings for your new {provider.toUpperCase()} pipe.
      </p>

      {validationErrors.length > 0 && (
        <div className="alert alert-danger">
          <strong>Validation Errors:</strong>
          <ul className="mb-0 mt-2">
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Network Configuration */}
      <div className="card shadow mb-4">
        <div className="card-body">
          <h6 className="mb-3">
            <i className="fas fa-network-wired mr-2"></i>
            Network Configuration
          </h6>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label font-weight-bold">
                Listen Address (Host:Port) <span className="text-danger">*</span>
              </label>
              <select
                className="form-control dropdown-styling"
                value={selectedHostPort}
                onChange={(e) => setSelectedHostPort(e.target.value)}
                disabled={!routingConfig || routingConfig.available_listen_addresses.length === 0}
              >
                {!routingConfig ? (
                  <option value="">Loading...</option>
                ) : routingConfig.available_listen_addresses.length === 0 ? (
                  <option value="">No addresses available</option>
                ) : (
                  <>
                    <option value="">-- Select listen address --</option>
                    {routingConfig.available_listen_addresses.map((addr) => (
                      <option key={addr} value={addr}>
                        {addr}
                      </option>
                    ))}
                  </>
                )}
              </select>
              <small className="form-text text-muted">
                Select the host:port this server will listen on
              </small>
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label font-weight-bold">
                Pipe Prefix <span className="text-danger">*</span>
              </label>
              <select
                className="form-control dropdown-styling"
                value={selectedPrefix}
                onChange={(e) => setSelectedPrefix(e.target.value)}
                disabled={!routingConfig || routingConfig.pipe_path_prefix.length === 0}
              >
                {!routingConfig ? (
                  <option value="">Loading...</option>
                ) : routingConfig.pipe_path_prefix.length === 0 ? (
                  <option value="">No prefixes available</option>
                ) : (
                  <>
                    <option value="">-- Select prefix --</option>
                    {routingConfig.pipe_path_prefix.map((item) => (
                      <option key={item.id} value={item.prefix}>
                        {item.name}
                      </option>
                    ))}
                  </>
                )}
              </select>
              <small className="form-text text-muted">
                Select the path prefix for your pipe
              </small>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12 mb-3">
              <label className="form-label font-weight-bold">
                Custom Path <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={customPath.startsWith('/') ? customPath.substring(1) : customPath}
                onChange={(e) => setCustomPath(e.target.value)}
                placeholder="my/custom/path"
              />
              <small className="form-text text-muted">
                Your custom path (will be prefixed with {selectedPrefix || 'prefix'}/)
              </small>
            </div>
          </div>

          <div className="alert alert-info" style={{ fontSize: '16px' }}>
            <i className="fas fa-info-circle mr-2"></i>
            <strong>Pipe Route:</strong> {fullRouteDisplay}
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="d-flex justify-content-between mt-4">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onBack}
        >
          <i className="fas fa-arrow-left mr-2"></i>
          Back
        </button>
        <div>
          <button
            type="button"
            className="btn btn-outline-danger mr-2"
            onClick={onCancel}
          >
            <i className="fas fa-times mr-2"></i>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleNext}
          >
            Next
            <i className="fas fa-arrow-right ml-2"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoutingStep;
